import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslationApp } from "../../lib/i18next";
import { BatteryMedium } from "lucide-react";
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  NOTIFICATIONS_PANEL_GAP_FROM_BUTTON,
  NOTIFICATIONS_PANEL_RIGHT_MOBILE,
  getFabButtonRect,
} from "../../hooks/useNotificationsFabPosition";
import { getNotificationsPanelPlacement } from "../../utils/notificationsFabPlacement";
import {
  NO_TASKS,
  NO_TASKS_DESCRIPTION,
  ACTIVITY,
  BUTTON_CANCEL,
  BUTTON_DISCARD,
  BUTTON_DISCARD_ALL,
  DISCARD_ALL_ACTIVITIES_DESCRIPTION,
  DISCARD_ALL_ACTIVITIES_TITLE,
} from "../../i18n/keysTranslations";
import { hasDismissableActivityNotifications } from "../../utils/notifications";
import Button from "../Buttons/Button";
import { ConfirmAlert } from "./Alerts";
import CampaignAlertItem from "./CampaignAlertItem";
import BulkCampaignsAlertItem from "./BulkCampaignsAlertItem";
import GeneratingCreativeAlertItem from "./GeneratingCreativeAlertItem";
import GeneratingCustomReportAlertItem from "./GeneratingCustomReportAlertItem";
import AnalyzingImageLayersAlertItem from "./AnalyzingImageLayersAlertItem";
import AnalyzingCreativeTemplateAlertItem from "./AnalyzingCreativeTemplateAlertItem";
import ProducingCreativeTemplateAlertItem from "./ProducingCreativeTemplateAlertItem";
import ImageAnimationAlertItem from "./ImageAnimationAlertItem";
import TaskAlertTimestampContext from "./TaskAlertTimestampContext";
import {
  INPUT_COLOR_BORDER,
  NOTIFICATIONS_PANEL_BORDER,
} from "../../utils/colors";

import { LaunchError } from "../../utils/errors";
import { store } from "../../app/store";

/**
 * Notification item renderer based on type
 * @param {Object} notification - Notification object
 * @param {Function} onCloseAlert - Handler to close alert
 * @returns {JSX.Element|null} Rendered notification item
 */
const getNotificationItemKey = (notification) =>
  notification.jobID || notification.id;

const DISCARD_ALL_BUTTON_SX = {
  height: 32,
  fontSize: "0.75rem",
  minWidth: 100,
  textTransform: "none",
};

const CONFIRM_ALERT_MIN_Z_INDEX = 10000;

const renderNotificationItem = (notification, onCloseAlert) => {
  const itemKey = getNotificationItemKey(notification);

  switch (notification.type) {
    case "campaign":
      return (
        <Box key={itemKey}>
          <CampaignAlertItem alert={notification} onClose={onCloseAlert} />
        </Box>
      );
    case "generatingCreative":
      return (
        <Box key={itemKey}>
          <GeneratingCreativeAlertItem
            alert={notification}
            onClose={onCloseAlert}
          />
        </Box>
      );
    case "generatingCustomReport":
      return (
        <Box key={itemKey}>
          <GeneratingCustomReportAlertItem
            alert={notification}
            onClose={onCloseAlert}
          />
        </Box>
      );
    case "bulkCampaigns":
      return (
        <Box key={itemKey}>
          <BulkCampaignsAlertItem alert={notification} onClose={onCloseAlert} />
        </Box>
      );
    case "analyzingImageLayers":
      return (
        <Box key={itemKey}>
          <AnalyzingImageLayersAlertItem
            alert={notification}
            onClose={onCloseAlert}
          />
        </Box>
      );
    case "analyzingCreativeTemplate":
      return (
        <Box key={itemKey}>
          <AnalyzingCreativeTemplateAlertItem
            alert={notification}
            onClose={onCloseAlert}
          />
        </Box>
      );
    case "producingCreativeTemplate":
      return (
        <Box key={itemKey}>
          <ProducingCreativeTemplateAlertItem
            alert={notification}
            onClose={onCloseAlert}
          />
        </Box>
      );
    case "imageAnimation":
      return (
        <Box key={itemKey}>
          <ImageAnimationAlertItem
            alert={notification}
            onClose={onCloseAlert}
          />
        </Box>
      );
    default:
      console.warn(`Unknown notification type: ${notification.type}`);
      return null;
  }
};

/**
 * Notifications panel component
 * @param {Object} props
 * @param {boolean} props.isExpanded - Whether panel is expanded
 * @param {Array} props.activeAlerts - Array of active alerts
 * @param {Function} props.onClose - Handler to close panel
 * @param {Function} props.onCloseAlert - Handler to close individual alert
 * @param {Function|null} props.onCloseAllAlerts - Dismiss every finished alert
 * @param {number} props.zIndex - z-index value
 * @param {{ bottom: number, right: number }|null} props.fabPosition - FAB anchor position
 * @param {import("react").RefObject<HTMLElement>} props.buttonRef - Tasks button ref
 * @param {string|null} props.headerHint - Optional secondary caption under the Activity title
 */
export const NotificationsPanel = ({
  isExpanded,
  activeAlerts,
  onClose,
  onCloseAlert,
  onCloseAllAlerts = null,
  zIndex = 1300,
  fabPosition = null,
  buttonRef = null,
  headerHint = null,
}) => {
  const { t } = useTranslationApp();
  const isMobileViewport = useMediaQuery((theme) =>
    theme.breakpoints.down("md")
  );
  const panelContentRef = useRef(null);
  const isDiscardAllConfirmOpenRef = useRef(false);
  const [panelPlacement, setPanelPlacement] = useState(null);
  const canDiscardAll =
    Boolean(onCloseAllAlerts) &&
    hasDismissableActivityNotifications(activeAlerts);

  const onDiscardAll = async () => {
    if (!onCloseAllAlerts || isDiscardAllConfirmOpenRef.current) return;

    isDiscardAllConfirmOpenRef.current = true;

    try {
      const result = await ConfirmAlert({
        title: t(DISCARD_ALL_ACTIVITIES_TITLE),
        text: t(DISCARD_ALL_ACTIVITIES_DESCRIPTION),
        confirmButtonText: t(BUTTON_DISCARD),
        cancelButtonText: t(BUTTON_CANCEL),
        // Activity overlay is above the default SweetAlert stacking order.
        containerZIndex: Math.max(zIndex, CONFIRM_ALERT_MIN_Z_INDEX),
      });

      if (!result?.isConfirmed) return;

      onCloseAllAlerts();
    } catch (error) {
      store.dispatch(
        LaunchError(error, false, [], {
          functionName: "onDiscardAll",
          capture: "exception",
        })
      );
      console.error("Error confirming discard all activities", error);
    } finally {
      isDiscardAllConfirmOpenRef.current = false;
    }
  };

  const hasFabAnchor =
    fabPosition?.bottom != null && fabPosition?.right != null;

  const updatePanelPlacement = useCallback(() => {
    if (!hasFabAnchor || !isExpanded) {
      return;
    }

    const buttonRect =
      buttonRef?.current?.getBoundingClientRect() ??
      getFabButtonRect({
        bottom: fabPosition.bottom,
        right: fabPosition.right,
      });
    const panelRect = panelContentRef.current?.getBoundingClientRect();

    if (!panelRect?.width || !panelRect?.height) {
      return;
    }

    const placement = getNotificationsPanelPlacement({
      buttonRect,
      panelWidth: panelRect.width,
      panelHeight: panelRect.height,
      gap: NOTIFICATIONS_PANEL_GAP_FROM_BUTTON,
    });

    setPanelPlacement(placement);
  }, [
    buttonRef,
    fabPosition?.bottom,
    fabPosition?.right,
    hasFabAnchor,
    isExpanded,
  ]);

  useLayoutEffect(() => {
    if (!isExpanded) {
      setPanelPlacement(null);
      return undefined;
    }

    updatePanelPlacement();
    const rafId = window.requestAnimationFrame(() => {
      updatePanelPlacement();
    });

    return () => {
      window.cancelAnimationFrame(rafId);
    };
  }, [
    updatePanelPlacement,
    activeAlerts.length,
    canDiscardAll,
    headerHint,
    isMobileViewport,
    isExpanded,
  ]);

  useLayoutEffect(() => {
    if (!isExpanded || !hasFabAnchor) {
      return undefined;
    }

    const onResize = () => {
      updatePanelPlacement();
    };

    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, [hasFabAnchor, isExpanded, updatePanelPlacement]);

  return (
    <Box
      sx={{
        position: "fixed",
        ...(hasFabAnchor && panelPlacement
          ? {
              top: panelPlacement.top,
              left: panelPlacement.left,
            }
          : hasFabAnchor
            ? {
                visibility: "hidden",
                top: 0,
                left: 0,
              }
            : {
                top: 80,
                right: { xs: NOTIFICATIONS_PANEL_RIGHT_MOBILE, md: 24 },
              }),
        zIndex: zIndex,
        pointerEvents: isExpanded ? "auto" : "none",
      }}
    >
      <Collapse
        in={isExpanded}
        timeout={200}
        easing={{
          enter: "cubic-bezier(0.4, 0, 0.2, 1)",
          exit: "cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <Paper
          ref={panelContentRef}
          elevation={8}
          sx={{
            width: { xs: "calc(100vw - 32px)", md: 384 },
            maxWidth: { xs: "calc(100vw - 32px)", md: 384 },
            borderRadius: 3,
            border: `1px solid ${NOTIFICATIONS_PANEL_BORDER}`,
            bgcolor: "background.paper",
            overflow: "hidden",
            boxShadow:
              "0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)",
            transform: isExpanded ? "translateX(0)" : "translateX(16px)",
            opacity: isExpanded ? 1 : 0,
            transition: "transform 200ms ease-out, opacity 200ms ease-out",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: headerHint ? "flex-start" : "center",
              justifyContent: "space-between",
              gap: 1,
              ...(activeAlerts.length > 0 && {
                borderBottom: `1px solid ${INPUT_COLOR_BORDER}`,
              }),
              px: 2,
              py: 1.5,
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  color: "text.primary",
                }}
              >
                {activeAlerts.length > 0 ? t(ACTIVITY) : ""}
              </Typography>
              {headerHint ? (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 0.25,
                    fontSize: "0.75rem",
                    color: "text.secondary",
                  }}
                >
                  {headerHint}
                </Typography>
              ) : null}
            </Box>
            <IconButton
              onClick={onClose}
              size="small"
              aria-label="close"
              sx={{
                color: "text.secondary",
                borderRadius: 1.5,
                p: 0.5,
                flexShrink: 0,
                "&:hover": {
                  bgcolor: "action.hover",
                  color: "text.primary",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>

          <Box
            sx={{
              maxHeight: 320,
              overflowY: "auto",
              p: 1.5,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            {activeAlerts.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  pb: 3,
                  textAlign: "center",
                }}
              >
                <BatteryMedium size={48} strokeWidth={1.5} />
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    mt: 1.5,
                    color: "text.primary",
                  }}
                >
                  {t(NO_TASKS)}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.75rem",
                    mt: 0.5,
                    color: "text.secondary",
                  }}
                >
                  {t(NO_TASKS_DESCRIPTION)}
                </Typography>
              </Box>
            ) : (
              activeAlerts.map((notification) => (
                <TaskAlertTimestampContext.Provider
                  key={getNotificationItemKey(notification)}
                  value={notification.creationTime ?? notification.timestamp}
                >
                  {renderNotificationItem(notification, onCloseAlert)}
                </TaskAlertTimestampContext.Provider>
              ))
            )}
          </Box>
          {canDiscardAll ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                borderTop: `1px solid ${INPUT_COLOR_BORDER}`,
                px: 1.5,
                py: 1.25,
              }}
            >
              <Button
                onClick={onDiscardAll}
                size="small"
                color="secondary"
                type="button"
                ariaLabel={t(BUTTON_DISCARD_ALL)}
                sx={DISCARD_ALL_BUTTON_SX}
              >
                {t(BUTTON_DISCARD_ALL)}
              </Button>
            </Box>
          ) : null}
        </Paper>
      </Collapse>
    </Box>
  );
};

export default NotificationsPanel;
